import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Trash2, Calendar, Plus, Edit2, Save, X, Filter, ArrowUpDown, Search, Printer, CheckCircle, AlertTriangle, ShoppingCart, ArrowRight, FileText, Download, Package, Clock, CheckCircle2, History, ChevronDown, ChevronUp, Users, Settings } from 'lucide-react';
import CompletedOrders from './CompletedOrders';
import OrdersActiveUpload from './OrdersActiveUpload';
import SupplierOrdersReport from './SupplierOrdersReport';
import SupplierAliasManager from './SupplierAliasManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Orders = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [receivedDates, setReceivedDates] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  // Sorting state - simple single sort by default
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [doubleSortEnabled, setDoubleSortEnabled] = useState(false);
  const [secondarySort, setSecondarySort] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('suggestions'); // 'suggestions', 'active' or 'completed'
  const [showSupplierReport, setShowSupplierReport] = useState(false);
  const [showAliasManager, setShowAliasManager] = useState(false);
  const [stockData, setStockData] = useState([]);
  
  // Company Filter State
  const [companyFilter, setCompanyFilter] = useState('all');
  const [availableCompanies, setAvailableCompanies] = useState(['WINLIFE PHARMA', 'POWERBOLIC LABS']);
  
  // Partial Receipt Modal State
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receivingOrder, setReceivingOrder] = useState(null);
  const [receiveQty, setReceiveQty] = useState(0);
  const [receiveDate, setReceiveDate] = useState('');
  const [receiveNotes, setReceiveNotes] = useState('');
  const [showReceiptHistory, setShowReceiptHistory] = useState(false);
  
  // Reorder Suggestions State
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [editingSuggestion, setEditingSuggestion] = useState(null);
  const [suggestionEditQty, setSuggestionEditQty] = useState(0);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showAddMoreChoiceModal, setShowAddMoreChoiceModal] = useState(false);
  const [duplicateOrderInfo, setDuplicateOrderInfo] = useState(null);
  const [pendingShift, setPendingShift] = useState(null);
  
  // Dormant Stock Alert State
  const [dormantAlerts, setDormantAlerts] = useState([]);
  const [dormantLoading, setDormantLoading] = useState(false);
  const [showDormantPanel, setShowDormantPanel] = useState(true);
  
  // Manual Suggestion State
  const [showManualSuggestionModal, setShowManualSuggestionModal] = useState(false);
  const [manualSuggestions, setManualSuggestions] = useState([]);
  const [newManualSuggestion, setNewManualSuggestion] = useState({
    product_name: '',
    suggested_qty: 0,
    priority: 'medium',
    reason: '',
    suggested_by: '',
    order_to: ''
  });
  
  // Product autocomplete state
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [searchingProducts, setSearchingProducts] = useState(false);
  
  // Supplier autocomplete state
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [searchingSuppliers, setSearchingSuppliers] = useState(false);
  
  // Order form product autocomplete state
  const [orderProductSuggestions, setOrderProductSuggestions] = useState([]);
  const [showOrderProductDropdown, setShowOrderProductDropdown] = useState(false);
  const [searchingOrderProducts, setSearchingOrderProducts] = useState(false);
  
  // Order form supplier autocomplete state
  const [orderSupplierSuggestions, setOrderSupplierSuggestions] = useState([]);
  const [showOrderSupplierDropdown, setShowOrderSupplierDropdown] = useState(false);
  const [searchingOrderSuppliers, setSearchingOrderSuppliers] = useState(false);
  
  // Suggestions sorting and filtering state
  const [suggestionSortConfig, setSuggestionSortConfig] = useState({ key: null, direction: 'asc' });
  const [suggestionFilters, setSuggestionFilters] = useState({
    product_name: '',
    priority: '',
    status: '' // 'ordered' or 'pending'
  });
  
  const [newOrder, setNewOrder] = useState({
    product_name: '',
    order_given_to: '',
    order_qty: 0,
    order_given_date: '',
    order_received_date: '',
    notes: ''
  });

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setOrders(response.data);
      
      const dates = {};
      response.data.forEach(order => {
        dates[order.id] = order.order_received_date || '';
      });
      setReceivedDates(dates);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Dormant Stock Alerts
  const fetchDormantAlerts = async () => {
    setDormantLoading(true);
    try {
      const response = await axios.get(`${API}/alerts/dormant-stock?days_threshold=60`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setDormantAlerts(response.data.alerts || []);
    } catch (error) {
      console.log('Could not fetch dormant alerts');
    } finally {
      setDormantLoading(false);
    }
  };

  // Fetch Reorder Suggestions
  const fetchSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const response = await axios.get(`${API}/alerts/reorder-suggestions?months_coverage=3`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setSuggestions(response.data.suggestions || []);
      
      // Also fetch manual suggestions and dormant alerts
      fetchManualSuggestions();
      fetchDormantAlerts();
    } catch (error) {
      toast.error('Failed to fetch reorder suggestions');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  // Fetch Manual Suggestions
  const fetchManualSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/alerts/manual-suggestions`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setManualSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.log('Could not fetch manual suggestions');
    }
  };

  // Add Manual Suggestion
  const handleAddManualSuggestion = async () => {
    if (!newManualSuggestion.product_name || newManualSuggestion.suggested_qty <= 0) {
      toast.error('Please enter product name and quantity');
      return;
    }

    try {
      await axios.post(`${API}/alerts/manual-suggestions`, newManualSuggestion, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      
      toast.success('Manual suggestion added!');
      setShowManualSuggestionModal(false);
      setNewManualSuggestion({
        product_name: '',
        suggested_qty: 0,
        priority: 'medium',
        reason: '',
        suggested_by: '',
        order_to: ''
      });
      fetchManualSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add suggestion');
    }
  };

  // Delete Manual Suggestion
  const handleDeleteManualSuggestion = async (suggestionId) => {
    try {
      await axios.delete(`${API}/alerts/manual-suggestions/${suggestionId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Suggestion removed');
      fetchManualSuggestions();
    } catch (error) {
      toast.error('Failed to remove suggestion');
    }
  };

  // Mark Manual Suggestion as Ordered
  const handleMarkManualSuggestionOrdered = async (suggestionId) => {
    try {
      await axios.put(`${API}/alerts/manual-suggestions/${suggestionId}/status?status=ordered`, {}, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      fetchManualSuggestions();
    } catch (error) {
      toast.error('Failed to update suggestion status');
    }
  };

  // Search products for autocomplete
  const searchProducts = async (query) => {
    if (query.length < 3) {
      setProductSuggestions([]);
      setShowProductDropdown(false);
      return;
    }

    setSearchingProducts(true);
    try {
      const response = await axios.get(`${API}/products/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setProductSuggestions(response.data.products || []);
      setShowProductDropdown(true);
    } catch (error) {
      console.log('Product search failed');
    } finally {
      setSearchingProducts(false);
    }
  };

  // Handle product name input change
  const handleProductNameChange = (value) => {
    setNewManualSuggestion(prev => ({ ...prev, product_name: value }));
    searchProducts(value);
  };

  // Select product from dropdown
  const selectProduct = (product) => {
    setNewManualSuggestion(prev => ({ 
      ...prev, 
      product_name: product.product_name 
    }));
    setShowProductDropdown(false);
    setProductSuggestions([]);
  };

  // Search suppliers for autocomplete in Add Order form
  const searchOrderSuppliers = async (query) => {
    if (query.length < 3) {
      setOrderSupplierSuggestions([]);
      setShowOrderSupplierDropdown(false);
      return;
    }

    setSearchingOrderSuppliers(true);
    try {
      const response = await axios.get(`${API}/suppliers/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setOrderSupplierSuggestions(response.data.suppliers || []);
      setShowOrderSupplierDropdown(true);
    } catch (error) {
      console.log('Supplier search failed');
    } finally {
      setSearchingOrderSuppliers(false);
    }
  };

  // Handle supplier input change for Add Order form (auto-uppercase)
  const handleOrderSupplierChange = (value) => {
    setNewOrder(prev => ({ ...prev, order_given_to: value.toUpperCase() }));
    searchOrderSuppliers(value);
  };

  // Select supplier from dropdown for Add Order form
  const selectOrderSupplier = (supplier) => {
    setNewOrder(prev => ({ 
      ...prev, 
      order_given_to: supplier.name.toUpperCase() 
    }));
    setShowOrderSupplierDropdown(false);
    setOrderSupplierSuggestions([]);
  };

  // Search products for autocomplete in Add Order form
  const searchOrderProducts = async (query) => {
    if (query.length < 3) {
      setOrderProductSuggestions([]);
      setShowOrderProductDropdown(false);
      return;
    }

    setSearchingOrderProducts(true);
    try {
      const response = await axios.get(`${API}/products/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setOrderProductSuggestions(response.data.products || []);
      setShowOrderProductDropdown(true);
    } catch (error) {
      console.log('Product search failed');
    } finally {
      setSearchingOrderProducts(false);
    }
  };

  // Handle product input change for Add Order form
  const handleOrderProductChange = (value) => {
    setNewOrder(prev => ({ ...prev, product_name: value }));
    searchOrderProducts(value);
  };

  // Select product from dropdown for Add Order form
  const selectOrderProduct = (product) => {
    setNewOrder(prev => ({ 
      ...prev, 
      product_name: product.product_name 
    }));
    setShowOrderProductDropdown(false);
    setOrderProductSuggestions([]);
  };

  // Search suppliers for autocomplete in Manual Suggestion modal
  const searchManualSuppliers = async (query) => {
    if (query.length < 2) {
      setSupplierSuggestions([]);
      setShowSupplierDropdown(false);
      return;
    }

    setSearchingSuppliers(true);
    try {
      const response = await axios.get(`${API}/suppliers/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setSupplierSuggestions(response.data.suppliers || []);
      setShowSupplierDropdown(true);
    } catch (error) {
      console.log('Supplier search failed');
    } finally {
      setSearchingSuppliers(false);
    }
  };

  // Handle supplier input change for Manual Suggestion modal (auto-uppercase)
  const handleManualSupplierChange = (value) => {
    setNewManualSuggestion(prev => ({ ...prev, order_to: value.toUpperCase() }));
    searchManualSuppliers(value);
  };

  // Select supplier from dropdown for Manual Suggestion modal
  const selectManualSupplier = (supplier) => {
    setNewManualSuggestion(prev => ({ 
      ...prev, 
      order_to: supplier.name.toUpperCase() 
    }));
    setShowSupplierDropdown(false);
    setSupplierSuggestions([]);
  };

  // Fetch stock data for cost information
  const fetchStockData = async () => {
    try {
      const response = await axios.get(`${API}/stock/winlife`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setStockData(response.data || []);
    } catch (error) {
      // Stock data is optional, don't show error
      console.log('Could not fetch stock data for costs');
    }
  };

  // Check if product already has active order
  const checkDuplicateOrder = (productName) => {
    return orders.find(order => 
      order.product_name?.toLowerCase() === productName?.toLowerCase() && 
      !order.order_received_date
    );
  };

  // Handle suggestion sort
  const handleSuggestionSort = (key) => {
    setSuggestionSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filter and sort suggestions
  const filteredAndSortedSuggestions = useMemo(() => {
    let filtered = [...suggestions];
    
    // Apply company filter
    if (companyFilter && companyFilter !== 'all') {
      filtered = filtered.filter(s => 
        s.manufactured_by?.toUpperCase() === companyFilter.toUpperCase()
      );
    }
    
    // Apply filters
    if (suggestionFilters.product_name) {
      const search = suggestionFilters.product_name.toLowerCase();
      filtered = filtered.filter(s => s.product_name?.toLowerCase().includes(search));
    }
    
    if (suggestionFilters.priority) {
      filtered = filtered.filter(s => s.priority === suggestionFilters.priority);
    }
    
    if (suggestionFilters.status) {
      if (suggestionFilters.status === 'ordered') {
        filtered = filtered.filter(s => checkDuplicateOrder(s.product_name));
      } else if (suggestionFilters.status === 'pending') {
        filtered = filtered.filter(s => !checkDuplicateOrder(s.product_name));
      }
    }
    
    // Apply sorting
    if (suggestionSortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[suggestionSortConfig.key];
        let bVal = b[suggestionSortConfig.key];
        
        // Handle numeric values
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return suggestionSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        
        // Handle string values
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
        
        if (aVal < bVal) return suggestionSortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return suggestionSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  }, [suggestions, suggestionFilters, suggestionSortConfig, orders, companyFilter]);

  // Handle shift to active orders
  const handleShiftToActive = async (suggestion, qty) => {
    const existingOrder = checkDuplicateOrder(suggestion.product_name);
    
    if (existingOrder) {
      setDuplicateOrderInfo(existingOrder);
      setPendingShift({ suggestion, qty });
      setEditingSuggestion(null);  // Close edit modal first
      setShowDuplicateModal(true);
      return;
    }
    
    // Create new order
    await createOrderFromSuggestion(suggestion, qty);
  };

  // Create order from suggestion
  const createOrderFromSuggestion = async (suggestion, qty) => {
    try {
      const newOrderData = {
        product_name: suggestion.product_name,
        order_given_to: suggestion.order_given_to || suggestion.manufactured_by || '',
        order_qty: qty,
        order_given_date: new Date().toISOString().split('T')[0],
        order_received_date: '',
        notes: suggestion.is_manual 
          ? `Manual suggestion from ${suggestion.suggested_by || 'team'}: ${suggestion.reason || ''}`
          : `Auto-generated from reorder suggestion (${suggestion.months_of_stock?.toFixed(1) || 0} months stock remaining)`
      };

      await axios.post(`${API}/orders`, newOrderData, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });

      toast.success(`Order created for ${suggestion.product_name}`);
      
      // If it was a manual suggestion, mark it as ordered
      if (suggestion.is_manual && suggestion.manual_id) {
        handleMarkManualSuggestionOrdered(suggestion.manual_id);
      }
      
      // Remove from suggestions list
      setSuggestions(prev => prev.filter(s => s.product_name !== suggestion.product_name));
      
      // Refresh orders
      fetchOrders();
      
      // Close edit modal
      setEditingSuggestion(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  // Handle user clicking "Yes" to add more - show choice modal
  const handleYesAddMore = () => {
    setShowDuplicateModal(false);
    setShowAddMoreChoiceModal(true);
  };

  // Handle add more to existing order
  const handleAddToExistingOrder = async () => {
    if (!pendingShift || !duplicateOrderInfo) return;
    
    try {
      const updatedQty = (duplicateOrderInfo.order_qty || 0) + pendingShift.qty;
      
      await axios.put(`${API}/orders/${duplicateOrderInfo.id}`, {
        ...duplicateOrderInfo,
        order_qty: updatedQty,
        notes: `${duplicateOrderInfo.notes || ''} | Added ${pendingShift.qty} from reorder suggestion`
      }, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });

      toast.success(`Added ${pendingShift.qty} to existing order for ${pendingShift.suggestion.product_name}`);
      
      // Remove from suggestions
      setSuggestions(prev => prev.filter(s => s.product_name !== pendingShift.suggestion.product_name));
      
      // Refresh orders
      fetchOrders();
      
      // Close all modals
      closeAllModals();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  // Handle place new order (separate from existing)
  const handlePlaceNewOrder = async () => {
    if (!pendingShift) return;
    
    await createOrderFromSuggestion(pendingShift.suggestion, pendingShift.qty);
    closeAllModals();
  };

  // Handle "No" - delete suggestion
  const handleNoDeleteSuggestion = () => {
    if (!pendingShift) return;
    
    setSuggestions(prev => prev.filter(s => s.product_name !== pendingShift.suggestion.product_name));
    toast.success('Suggestion removed');
    
    closeAllModals();
  };

  // Close all modals helper
  const closeAllModals = () => {
    setShowDuplicateModal(false);
    setShowAddMoreChoiceModal(false);
    setPendingShift(null);
    setDuplicateOrderInfo(null);
    setEditingSuggestion(null);
  };

  // Open receive modal
  const openReceiveModal = (order) => {
    setReceivingOrder(order);
    setReceiveQty(order.order_qty - (order.received_qty || 0)); // Default to remaining qty
    setReceiveDate(new Date().toISOString().split('T')[0]);
    setReceiveNotes('');
    setShowReceiveModal(true);
    setShowReceiptHistory(false);
  };

  // Handle partial receipt
  const handleReceivePartial = async () => {
    if (!receivingOrder || receiveQty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    try {
      const response = await axios.post(`${API}/orders/${receivingOrder.id}/receive`, {
        qty: receiveQty,
        date: receiveDate,
        notes: receiveNotes
      }, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });

      if (response.data.auto_completed) {
        toast.success('Order fully received and moved to Completed!');
      } else {
        toast.success(`Received ${receiveQty} units. ${response.data.remaining} remaining.`);
      }

      setShowReceiveModal(false);
      setReceivingOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive stock');
    }
  };

  // Handle receive all remaining
  const handleReceiveAll = async () => {
    if (!receivingOrder) return;
    
    const remaining = receivingOrder.order_qty - (receivingOrder.received_qty || 0);
    setReceiveQty(remaining);
    
    try {
      const response = await axios.post(`${API}/orders/${receivingOrder.id}/receive`, {
        qty: remaining,
        date: receiveDate,
        notes: receiveNotes || 'Full order received'
      }, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });

      toast.success('Order fully received and moved to Completed!');
      setShowReceiveModal(false);
      setReceivingOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive stock');
    }
  };

  // Mark partial as complete (satisfied with partial)
  const handleCompletePartial = async () => {
    if (!receivingOrder) return;

    try {
      const response = await axios.post(`${API}/orders/${receivingOrder.id}/complete-partial`, {}, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });

      toast.success(`Order marked complete with partial receipt (${receivingOrder.received_qty}/${receivingOrder.order_qty})`);
      setShowReceiveModal(false);
      setReceivingOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete order');
    }
  };

  // Get status badge color
  const getStatusBadge = (order) => {
    const status = order.status || 'pending';
    const received = order.received_qty || 0;
    const total = order.order_qty || 0;
    
    if (status === 'completed' || received >= total) {
      return { color: 'bg-green-100 text-green-800', label: 'Complete', icon: CheckCircle2 };
    } else if (status === 'partial' || received > 0) {
      return { color: 'bg-orange-100 text-orange-800', label: `Partial (${received}/${total})`, icon: Clock };
    } else {
      return { color: 'bg-yellow-100 text-yellow-800', label: 'Pending', icon: Package };
    }
  };

  // Calculate order age in days
  const getOrderAge = (orderDate) => {
    if (!orderDate) return null;
    try {
      const given = new Date(orderDate);
      const today = new Date();
      const diffTime = today - given;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  // Get age badge color based on days
  const getAgeBadgeColor = (days) => {
    if (days === null) return 'bg-slate-100 text-slate-600';
    if (days <= 7) return 'bg-green-100 text-green-700';
    if (days <= 14) return 'bg-yellow-100 text-yellow-700';
    if (days <= 30) return 'bg-orange-100 text-orange-700';
    return 'bg-blue-100 text-blue-700';
  };

  useEffect(() => {
    fetchOrders();
    fetchSuggestions();
    fetchStockData();
  }, []);

  // Listen for pull-to-refresh event from parent
  useEffect(() => {
    const handler = () => {
      fetchOrders();
      fetchSuggestions();
      fetchStockData();
    };
    window.addEventListener('app:refresh-data', handler);
    return () => window.removeEventListener('app:refresh-data', handler);
  }, []);

  // Simple sorting - with optional double sort mode
  const handleSort = (key) => {
    if (doubleSortEnabled && sortConfig.key && sortConfig.key !== key) {
      // Double sort mode: set as secondary sort
      setSecondarySort(prev => ({
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
      }));
    } else {
      // Normal mode: set as primary sort
      setSortConfig(prev => ({
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
      }));
      // Clear secondary if clicking same key
      if (key === secondarySort.key) {
        setSecondarySort({ key: null, direction: 'asc' });
      }
    }
  };

  // Clear all sorting
  const clearSort = () => {
    setSortConfig({ key: null, direction: 'asc' });
    setSecondarySort({ key: null, direction: 'asc' });
    setDoubleSortEnabled(false);
  };

  // Toggle double sort mode
  const toggleDoubleSort = () => {
    if (doubleSortEnabled) {
      // Turning off: clear secondary sort
      setSecondarySort({ key: null, direction: 'asc' });
    }
    setDoubleSortEnabled(!doubleSortEnabled);
  };

  // Get sort indicator for column header
  const getSortIndicator = (key) => {
    const isPrimary = sortConfig.key === key;
    const isSecondary = secondarySort.key === key;
    
    if (isPrimary) {
      return (
        <span className="ml-1 inline-flex items-center">
          <span className={`${doubleSortEnabled ? 'bg-blue-600' : 'bg-slate-600'} text-white text-[10px] px-1.5 py-0.5 rounded font-bold`}>
            {doubleSortEnabled ? '1' : ''}{sortConfig.direction === 'asc' ? '↑' : '↓'}
          </span>
        </span>
      );
    }
    if (isSecondary && doubleSortEnabled) {
      return (
        <span className="ml-1 inline-flex items-center">
          <span className="bg-purple-600 text-white text-[10px] px-1.5 py-0.5 rounded font-bold">
            2{secondarySort.direction === 'asc' ? '↑' : '↓'}
          </span>
        </span>
      );
    }
    return null;
  };

  // Compare function for sorting
  const compareValues = (a, b, key, direction) => {
    // Special handling for order_age
    if (key === 'order_age') {
      const aAge = getOrderAge(a.order_given_date) ?? -1;
      const bAge = getOrderAge(b.order_given_date) ?? -1;
      return direction === 'asc' ? aAge - bAge : bAge - aAge;
    }

    const aVal = a[key];
    const bVal = b[key];

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    const aStr = String(aVal || '').toLowerCase();
    const bStr = String(bVal || '').toLowerCase();
    
    if (aStr < bStr) return direction === 'asc' ? -1 : 1;
    if (aStr > bStr) return direction === 'asc' ? 1 : -1;
    return 0;
  };

  const filteredAndSortedOrders = useMemo(() => {
    // First filter by search term
    let filtered = orders;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = orders.filter(order =>
        order.product_name?.toLowerCase().includes(search) ||
        order.order_given_to?.toLowerCase().includes(search) ||
        order.notes?.toLowerCase().includes(search)
      );
    }

    // Filter by company
    if (companyFilter && companyFilter !== 'all') {
      filtered = filtered.filter(order => 
        order.manufactured_by?.toUpperCase() === companyFilter.toUpperCase()
      );
    }

    // No sorting if no primary key
    if (!sortConfig.key) return filtered;

    // Sort with optional secondary
    return [...filtered].sort((a, b) => {
      // Primary sort
      const primaryResult = compareValues(a, b, sortConfig.key, sortConfig.direction);
      
      // If primary values are equal and secondary sort exists (double sort mode)
      if (primaryResult === 0 && doubleSortEnabled && secondarySort.key) {
        return compareValues(a, b, secondarySort.key, secondarySort.direction);
      }
      
      return primaryResult;
    });
  }, [orders, sortConfig, secondarySort, doubleSortEnabled, searchTerm, companyFilter]);

  const handleReceivedDateChange = (orderId, value) => {
    setReceivedDates(prev => ({ ...prev, [orderId]: value }));
  };

  const saveReceivedDate = async (orderId) => {
    const dateValue = receivedDates[orderId];
    
    // Validate date is entered
    if (!dateValue || dateValue.trim() === '') {
      toast.error('Please select a date first');
      return;
    }
    
    try {
      // Ensure date is in YYYY-MM-DD format
      const formattedDate = dateValue.split('T')[0]; // Remove time if present
      
      const response = await axios.put(
        `${API}/orders/${orderId}?order_received_date=${encodeURIComponent(formattedDate)}`,
        null,
        { headers: { 'Authorization': `Bearer ${user.token}` } }
      );
      
      // Check if order was moved to completed
      if (response.data && response.data.completed) {
        toast.success('Order marked as completed and moved to Completed Orders panel');
      } else {
        toast.success('Order received date updated');
      }
      
      // Clear the received date input for this order
      setReceivedDates(prev => {
        const updated = { ...prev };
        delete updated[orderId];
        return updated;
      });
      
      fetchOrders();
    } catch (error) {
      console.error('Save error:', error);
      let errorMsg = 'Failed to update order';
      
      if (error.response?.data?.detail) {
        // Handle both string and array/object error details
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    
    try {
      await axios.delete(`${API}/orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Order deleted successfully');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const handleCreateOrder = (data) => {
    setNewOrder({
      product_name: data.product_name || '',
      order_given_to: data.manufactured_by || '',
      order_qty: data.suggested_order_qty || 0,
      order_given_date: new Date().toISOString().split('T')[0],
      order_received_date: '',
      notes: ''
    });
    setShowAddForm(true);
  };

  const handleAddOrder = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/orders`, newOrder, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Order added successfully');
      setShowAddForm(false);
      setNewOrder({
        product_name: '',
        order_given_to: '',
        order_qty: 0,
        order_given_date: '',
        order_received_date: '',
        notes: ''
      });
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add order');
    }
  };

  const startEdit = (order) => {
    setEditingId(order.id);
    setEditData({
      product_name: order.product_name,
      order_given_to: order.order_given_to,
      order_qty: order.order_qty,
      order_given_date: order.order_given_date,
      notes: order.notes || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, editData, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Order updated successfully');
      setEditingId(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-slate-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-slate-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead {
            display: table-header-group;
          }
          .overflow-x-auto {
            max-height: none !important;
            overflow: visible !important;
          }
          .sticky {
            position: static !important;
          }
          @page {
            size: landscape;
            margin: 0.5cm;
          }
        }
      `}</style>
      {/* Tab Buttons */}
      <div className="flex gap-2 mb-6 no-print flex-wrap">
        <Button
          onClick={() => setViewMode('suggestions')}
          variant={viewMode === 'suggestions' ? 'default' : 'outline'}
          size="sm"
          className={viewMode === 'suggestions' ? 'bg-amber-600 hover:bg-amber-700' : ''}
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Reorder Suggestions
          {suggestions.length > 0 && (
            <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              {suggestions.length}
            </span>
          )}
        </Button>
        <Button
          onClick={() => setViewMode('active')}
          variant={viewMode === 'active' ? 'default' : 'outline'}
          size="sm"
        >
          <Calendar className="w-4 h-4 mr-2" />
          Active Orders
        </Button>
        <Button
          onClick={() => setViewMode('completed')}
          variant={viewMode === 'completed' ? 'default' : 'outline'}
          size="sm"
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          Completed Orders
        </Button>
        
        {/* Company Filter Dropdown */}
        <div className="ml-4 flex items-center gap-2">
          <span className="text-sm text-slate-500">Company:</span>
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="all">All Companies</option>
            {availableCompanies.map(company => (
              <option key={company} value={company}>{company}</option>
            ))}
          </select>
          {companyFilter !== 'all' && (
            <button 
              onClick={() => setCompanyFilter('all')}
              className="text-slate-400 hover:text-red-500"
              title="Clear filter"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Supplier Alias Manager Button - Admin Only */}
        {user.role === 'admin' && (
          <Button
            onClick={() => setShowAliasManager(true)}
            variant="outline"
            size="sm"
            className="ml-auto text-indigo-600 border-indigo-300 hover:bg-indigo-50"
            data-testid="supplier-alias-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Supplier Aliases</span>
          </Button>
        )}
      </div>

      {/* Supplier Alias Manager Modal */}
      {showAliasManager && (
        <SupplierAliasManager user={user} onClose={() => setShowAliasManager(false)} />
      )}

      {/* Step 1: Duplicate Order Confirmation Modal - "Already ordered, add more?" */}
      {showDuplicateModal && duplicateOrderInfo && pendingShift && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                Already Ordered
              </h3>
            </div>
            <div className="p-6">
              <p className="text-slate-700 mb-4">
                <strong>{pendingShift.suggestion.product_name}</strong> already has an active order:
              </p>
              <div className="bg-slate-100 rounded-lg p-4 mb-4">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold">Current Order Qty:</span> {duplicateOrderInfo.order_qty?.toLocaleString()}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-semibold">Given To:</span> {duplicateOrderInfo.order_given_to}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-semibold">Order Date:</span> {duplicateOrderInfo.order_given_date}
                </p>
              </div>
              <p className="text-lg text-slate-800 font-medium mb-6 text-center">
                Would you like to add more order?
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={handleYesAddMore}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Yes, Add More
                </Button>
                <Button
                  onClick={handleNoDeleteSuggestion}
                  variant="outline"
                  className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  No, Delete Suggestion
                </Button>
              </div>
              <Button
                onClick={closeAllModals}
                variant="ghost"
                className="w-full mt-3"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Choice Modal - "Add to existing OR Place new order?" */}
      {showAddMoreChoiceModal && pendingShift && duplicateOrderInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <ShoppingCart className="w-6 h-6" />
                How would you like to add?
              </h3>
            </div>
            <div className="p-6">
              <p className="text-slate-700 mb-2">
                Adding <strong>{pendingShift.qty?.toLocaleString()}</strong> units of <strong>{pendingShift.suggestion.product_name}</strong>
              </p>
              <p className="text-slate-500 text-sm mb-6">
                Existing order has {duplicateOrderInfo.order_qty?.toLocaleString()} units
              </p>
              
              <div className="space-y-3">
                <Button
                  onClick={handleAddToExistingOrder}
                  className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-left justify-start"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-lg">
                      <Plus className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold">Add to Existing Order</p>
                      <p className="text-blue-200 text-sm">New total: {((duplicateOrderInfo.order_qty || 0) + pendingShift.qty).toLocaleString()} units</p>
                    </div>
                  </div>
                </Button>
                
                <Button
                  onClick={handlePlaceNewOrder}
                  variant="outline"
                  className="w-full py-6 text-left justify-start border-2"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-slate-100 p-2 rounded-lg">
                      <ArrowRight className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-700">Place New Order</p>
                      <p className="text-slate-500 text-sm">Create separate order for {pendingShift.qty?.toLocaleString()} units</p>
                    </div>
                  </div>
                </Button>
              </div>
              
              <Button
                onClick={closeAllModals}
                variant="ghost"
                className="w-full mt-4"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Suggestion Modal */}
      {editingSuggestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4">
              <h3 className="text-xl font-bold text-white">Edit Reorder Suggestion</h3>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Product Name</label>
                <p className="text-lg font-semibold text-slate-900">{editingSuggestion.product_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Current Stock</label>
                  <p className="text-slate-600">{editingSuggestion.current_stock}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Sale</label>
                  <p className="text-slate-600">{editingSuggestion.monthly_sale}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Months of Stock</label>
                  <p className="text-slate-600">{editingSuggestion.months_of_stock?.toFixed(1) || 0}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Manufacturer</label>
                  <p className="text-slate-600">{editingSuggestion.manufactured_by || '-'}</p>
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-1">Order Quantity</label>
                <Input
                  type="number"
                  value={suggestionEditQty}
                  onChange={(e) => setSuggestionEditQty(parseInt(e.target.value) || 0)}
                  min="1"
                  className="w-full"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Suggested: {editingSuggestion.suggested_order_qty} (for 3 months coverage)
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => handleShiftToActive(editingSuggestion, suggestionEditQty)}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={suggestionEditQty <= 0}
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Shift to Active Orders
                </Button>
                <Button
                  onClick={() => {
                    setSuggestions(prev => prev.filter(s => s.product_name !== editingSuggestion.product_name));
                    setEditingSuggestion(null);
                    toast.success('Suggestion removed');
                  }}
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
              <Button
                onClick={() => setEditingSuggestion(null)}
                variant="ghost"
                className="w-full mt-3"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'suggestions' ? (
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-white">Reorder Suggestions</h2>
              <p className="text-amber-100 text-sm mt-1">
                Auto suggestions + Manual suggestions from team
                {manualSuggestions.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                    {manualSuggestions.length} manual
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowManualSuggestionModal(true)}
                size="sm"
                className="bg-white text-amber-600 hover:bg-amber-50"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Manual
              </Button>
              <Button
                onClick={fetchSuggestions}
                variant="outline"
                size="sm"
                className="bg-white/20 text-white border-white/30 hover:bg-white/30"
              >
                Refresh
              </Button>
            </div>
          </div>
          
          {/* Manual Suggestions Section */}
          {manualSuggestions.length > 0 && (
            <div className="bg-purple-50 border-b border-purple-200 p-4">
              <h3 className="text-sm font-semibold text-purple-800 flex items-center gap-2 mb-3">
                <Users className="w-4 h-4" />
                Manual Suggestions from Team ({manualSuggestions.length})
              </h3>
              <div className="grid gap-2">
                {manualSuggestions.map((ms) => {
                  const existingOrder = checkDuplicateOrder(ms.product_name);
                  return (
                    <div 
                      key={ms.id} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        existingOrder ? 'bg-green-50 border-green-200' : 'bg-white border-purple-200'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">{ms.product_name}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            ms.priority === 'critical' ? 'bg-red-100 text-red-700' :
                            ms.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                            ms.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {ms.priority}
                          </span>
                          {existingOrder && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                              Already Ordered
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-600 mt-1">
                          Qty: <strong>{ms.suggested_qty?.toLocaleString()}</strong>
                          {ms.order_to && <span className="ml-3">To: {ms.order_to}</span>}
                          {ms.suggested_by && <span className="ml-3 text-purple-600">By: {ms.suggested_by}</span>}
                        </div>
                        {ms.reason && (
                          <p className="text-xs text-slate-500 mt-1 italic">"{ms.reason}"</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!existingOrder ? (
                          <Button
                            onClick={() => {
                              // Pre-fill the shift to active orders
                              setEditingSuggestion({
                                product_name: ms.product_name,
                                suggested_order_qty: ms.suggested_qty,
                                order_given_to: ms.order_to || '',
                                is_manual: true,
                                manual_id: ms.id
                              });
                              setSuggestionEditQty(ms.suggested_qty);
                            }}
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                          >
                            <ArrowRight className="w-3 h-3 mr-1" />
                            Order Now
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleDeleteManualSuggestion(ms.id)}
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-300 text-xs"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Done
                          </Button>
                        )}
                        <Button
                          onClick={() => handleDeleteManualSuggestion(ms.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Dormant Stock Alert Section */}
          {dormantAlerts.length > 0 && (
            <div className="bg-red-50 border-b border-red-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-red-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Dormant Stock Alert ({dormantAlerts.filter(a => companyFilter === 'all' || a.manufactured_by?.toUpperCase() === companyFilter.toUpperCase()).length})
                  <span className="text-xs font-normal text-red-600">
                    Products out of stock for 60+ days
                  </span>
                </h3>
                <button 
                  onClick={() => setShowDormantPanel(!showDormantPanel)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  {showDormantPanel ? 'Hide' : 'Show'}
                </button>
              </div>
              {showDormantPanel && (
                <div className="grid gap-2 max-h-64 overflow-y-auto">
                  {dormantAlerts
                    .filter(a => companyFilter === 'all' || a.manufactured_by?.toUpperCase() === companyFilter.toUpperCase())
                    .slice(0, 10).map((alert, idx) => {
                    const existingOrder = checkDuplicateOrder(alert.product_name);
                    return (
                      <div 
                        key={idx} 
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          existingOrder ? 'bg-green-50 border-green-200' : 'bg-white border-red-200'
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800">{alert.product_name}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              alert.severity === 'critical' ? 'bg-red-100 text-red-700' :
                              alert.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {alert.severity}
                            </span>
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                              {alert.days_out_of_stock}+ days out
                            </span>
                            {existingOrder && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                                Already Ordered
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            Stock: {alert.current_stock} | Was selling: ~{alert.historical_avg_sale}/month | Suggested: {alert.suggested_order_qty}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {!existingOrder ? (
                            <Button
                              onClick={() => handleCreateOrder({
                                product_name: alert.product_name,
                                suggested_order_qty: alert.suggested_order_qty,
                                manufactured_by: alert.manufactured_by
                              })}
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white text-xs"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Order Now
                            </Button>
                          ) : (
                            <span className="text-xs text-green-600 font-medium px-2 py-1">
                              Order Placed
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {dormantAlerts.length > 10 && (
                    <p className="text-center text-red-600 text-sm py-2">
                      +{dormantAlerts.length - 10} more dormant products
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {suggestionsLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <svg className="animate-spin h-12 w-12 text-amber-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-slate-600">Loading suggestions...</p>
              </div>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="py-16 text-center">
              <ShoppingCart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">No reorder suggestions at this time</p>
              <p className="text-slate-400 text-sm mt-1">All products have sufficient stock levels</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Filter Row */}
              <div className="bg-slate-100 px-4 py-3 border-b border-slate-200 flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-600">Filters:</span>
                </div>
                <Input
                  type="text"
                  placeholder="Search product..."
                  value={suggestionFilters.product_name}
                  onChange={(e) => setSuggestionFilters(prev => ({ ...prev, product_name: e.target.value }))}
                  className="w-48 h-8 text-sm"
                />
                <select
                  value={suggestionFilters.priority}
                  onChange={(e) => setSuggestionFilters(prev => ({ ...prev, priority: e.target.value }))}
                  className="h-8 px-2 text-sm border border-slate-300 rounded-md bg-white"
                >
                  <option value="">All Priority</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                <select
                  value={suggestionFilters.status}
                  onChange={(e) => setSuggestionFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="h-8 px-2 text-sm border border-slate-300 rounded-md bg-white"
                >
                  <option value="">All Status</option>
                  <option value="ordered">✓ Ordered</option>
                  <option value="pending">Pending</option>
                </select>
                {(suggestionFilters.product_name || suggestionFilters.priority || suggestionFilters.status) && (
                  <Button
                    onClick={() => setSuggestionFilters({ product_name: '', priority: '', status: '' })}
                    variant="ghost"
                    size="sm"
                    className="h-8 text-slate-500 hover:text-slate-700"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                )}
                <span className="text-sm text-slate-500 ml-auto">
                  Showing {filteredAndSortedSuggestions.length} of {suggestions.length}
                </span>
              </div>
              
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th 
                      className="px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSuggestionSort('product_name')}
                    >
                      <div className="flex items-center gap-1">
                        Product Name
                        <ArrowUpDown className={`w-4 h-4 ${suggestionSortConfig.key === 'product_name' ? 'text-blue-600' : 'text-slate-400'}`} />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSuggestionSort('current_stock')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Current Stock
                        <ArrowUpDown className={`w-4 h-4 ${suggestionSortConfig.key === 'current_stock' ? 'text-blue-600' : 'text-slate-400'}`} />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSuggestionSort('monthly_sale')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Monthly Sale
                        <ArrowUpDown className={`w-4 h-4 ${suggestionSortConfig.key === 'monthly_sale' ? 'text-blue-600' : 'text-slate-400'}`} />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSuggestionSort('months_of_stock')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Months Left
                        <ArrowUpDown className={`w-4 h-4 ${suggestionSortConfig.key === 'months_of_stock' ? 'text-blue-600' : 'text-slate-400'}`} />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-sm font-semibold text-green-700 cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSuggestionSort('suggested_order_qty')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Suggested Qty
                        <ArrowUpDown className={`w-4 h-4 ${suggestionSortConfig.key === 'suggested_order_qty' ? 'text-blue-600' : 'text-slate-400'}`} />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-center text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSuggestionSort('priority')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Status
                        <ArrowUpDown className={`w-4 h-4 ${suggestionSortConfig.key === 'priority' ? 'text-blue-600' : 'text-slate-400'}`} />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredAndSortedSuggestions.map((suggestion, idx) => {
                    const existingOrder = checkDuplicateOrder(suggestion.product_name);
                    const hasActiveOrder = !!existingOrder;
                    
                    return (
                      <tr 
                        key={idx} 
                        className={`hover:bg-slate-50 transition-colors ${hasActiveOrder ? 'bg-green-50/50' : ''}`}
                        title={hasActiveOrder ? `Order placed: ${existingOrder.order_qty?.toLocaleString()} units to ${existingOrder.order_given_to || 'N/A'}` : ''}
                      >
                        <td className={`px-4 py-3 font-medium ${hasActiveOrder ? 'text-slate-400' : 'text-slate-900'}`}>
                          <span className={hasActiveOrder ? 'line-through' : ''}>{suggestion.product_name}</span>
                          {hasActiveOrder && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Order Placed
                            </span>
                          )}
                        </td>
                        <td className={`px-4 py-3 text-right ${hasActiveOrder ? 'line-through text-slate-400' : 'text-slate-600'}`}>{suggestion.current_stock?.toLocaleString()}</td>
                        <td className={`px-4 py-3 text-right ${hasActiveOrder ? 'line-through text-slate-400' : 'text-slate-600'}`}>{suggestion.monthly_sale?.toLocaleString()}</td>
                        <td className={`px-4 py-3 text-right ${hasActiveOrder ? 'line-through text-slate-400' : 'text-slate-600'}`}>{suggestion.months_of_stock?.toFixed(1)}</td>
                        <td className={`px-4 py-3 text-right font-bold ${hasActiveOrder ? 'line-through text-slate-400' : 'text-green-700'}`}>{suggestion.suggested_order_qty?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-center">
                          {hasActiveOrder ? (
                            <span 
                              className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 cursor-help"
                              title={`Qty: ${existingOrder.order_qty?.toLocaleString()} | To: ${existingOrder.order_given_to || 'N/A'} | Date: ${existingOrder.order_given_date || 'N/A'}`}
                            >
                              ✓ Ordered
                            </span>
                          ) : (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              suggestion.priority === 'critical' ? 'bg-red-100 text-red-700' :
                              suggestion.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {suggestion.priority}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            onClick={() => {
                              setEditingSuggestion(suggestion);
                              setSuggestionEditQty(suggestion.suggested_order_qty || 0);
                            }}
                            size="sm"
                            variant="outline"
                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          >
                            <Edit2 className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : viewMode === 'completed' ? (
        <CompletedOrders user={user} companyFilter={companyFilter} />
      ) : (
        <>
          {/* Mobile-friendly toolbar */}
          <div className="flex flex-col gap-3 mb-4 no-print">
            {/* Row 1: Search */}
            <div className="flex items-center gap-2">
              <Input
                icon={<Search className="w-4 h-4" />}
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 sm:w-48 lg:w-64 sm:flex-none"
              />
            </div>
            
            {/* Row 2: Action buttons - wrap on mobile */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Double Sort Toggle Button */}
              <Button
                onClick={toggleDoubleSort}
                size="sm"
                variant={doubleSortEnabled ? "default" : "outline"}
                className={`text-xs sm:text-sm ${doubleSortEnabled ? "bg-purple-600 hover:bg-purple-700" : "text-purple-600 border-purple-300 hover:bg-purple-50"}`}
              >
                <ArrowUpDown className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">{doubleSortEnabled ? "2-Sort ON" : "2-Sort"}</span>
              </Button>
              {/* Print Button */}
              <Button
                onClick={handlePrint}
                variant="outline"
                size="sm"
                className="text-xs sm:text-sm"
                data-testid="print-orders-btn"
              >
                <Printer className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Print</span>
              </Button>
              {/* Supplier Report Button */}
              <Button
                onClick={() => setShowSupplierReport(true)}
                variant="outline"
                size="sm"
                className="text-blue-600 border-blue-300 hover:bg-blue-50 text-xs sm:text-sm"
                data-testid="supplier-report-btn"
              >
                <FileText className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Supplier Report</span>
              </Button>
              {/* Upload Button */}
              <OrdersActiveUpload user={user} onUploadSuccess={fetchOrders} />
              {/* Add Order Button */}
              {(user.role === 'admin' || user.role === 'winlife') && (
                <Button
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm"
                  data-testid="add-order-btn"
                >
                  <Plus className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Add Order</span>
                </Button>
              )}
            </div>
          </div>
          
          {/* Sort Info & Clear - Separate row for better mobile display */}
          {(sortConfig.key || secondarySort.key) && (
            <div className="flex items-center gap-2 text-xs mb-3 flex-wrap no-print">
              <span className="text-slate-500">Sort:</span>
              {sortConfig.key && (
                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  {doubleSortEnabled ? '1: ' : ''}{sortConfig.key.replace(/_/g, ' ')} {sortConfig.direction === 'asc' ? '↑' : '↓'}
                </span>
              )}
              {secondarySort.key && doubleSortEnabled && (
                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  2: {secondarySort.key.replace(/_/g, ' ')} {secondarySort.direction === 'asc' ? '↑' : '↓'}
                </span>
              )}
              <button onClick={clearSort} className="text-slate-400 hover:text-red-500" title="Clear sort">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {showAddForm && (
        <form onSubmit={handleAddOrder} className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200 no-print">
          <h3 className="text-lg font-semibold mb-4">Add New Order</h3>
          <div className="grid grid-cols-2 gap-4">
            {/* Product Name Autocomplete */}
            <div className="relative">
              <Input
                placeholder="Product Name (min 3 chars)"
                value={newOrder.product_name}
                onChange={(e) => handleOrderProductChange(e.target.value)}
                onFocus={() => newOrder.product_name.length >= 3 && setShowOrderProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowOrderProductDropdown(false), 200)}
                required
                autoComplete="off"
                data-testid="add-order-product-name"
              />
              {searchingOrderProducts && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {/* Product Autocomplete Dropdown */}
              {showOrderProductDropdown && orderProductSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {orderProductSuggestions.map((product, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => selectOrderProduct(product)}
                      className="w-full px-3 py-2 text-left hover:bg-green-50 flex items-center justify-between border-b border-slate-100 last:border-0"
                    >
                      <span className="font-medium text-slate-800">{product.product_name}</span>
                      <span className="text-xs text-slate-500">
                        Stock: {product.current_stock?.toLocaleString() || 0}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {newOrder.product_name.length > 0 && newOrder.product_name.length < 3 && (
                <p className="text-xs text-slate-400 mt-1">Type at least 3 characters</p>
              )}
            </div>
            {/* Supplier Autocomplete */}
            <div className="relative">
              <Input
                placeholder="Order Given To (Supplier)"
                value={newOrder.order_given_to}
                onChange={(e) => handleOrderSupplierChange(e.target.value)}
                onFocus={() => newOrder.order_given_to.length >= 3 && setShowOrderSupplierDropdown(true)}
                onBlur={() => setTimeout(() => setShowOrderSupplierDropdown(false), 200)}
                required
                autoComplete="off"
                data-testid="add-order-supplier"
              />
              {searchingOrderSuppliers && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {/* Supplier Autocomplete Dropdown */}
              {showOrderSupplierDropdown && orderSupplierSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {orderSupplierSuggestions.map((supplier, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => selectOrderSupplier(supplier)}
                      className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center justify-between border-b border-slate-100 last:border-0"
                    >
                      <span className="font-medium text-slate-800">{supplier.name}</span>
                      {supplier.has_ledger && (
                        <span className={`text-xs ${supplier.ledger_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          Bal: {supplier.ledger_balance?.toLocaleString() || 0}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {newOrder.order_given_to.length > 0 && newOrder.order_given_to.length < 3 && (
                <p className="text-xs text-slate-400 mt-1">Type at least 3 characters for suggestions</p>
              )}
            </div>
            <Input
              type="number"
              placeholder="Order Qty"
              value={newOrder.order_qty}
              onChange={(e) => setNewOrder({...newOrder, order_qty: parseFloat(e.target.value)})}
              required
              data-testid="add-order-qty"
            />
            <Input
              type="date"
              placeholder="Order Given Date"
              value={newOrder.order_given_date}
              onChange={(e) => setNewOrder({...newOrder, order_given_date: e.target.value})}
              required
              data-testid="add-order-date"
            />
            <Input
              type="date"
              placeholder="Order Received Date (Optional)"
              value={newOrder.order_received_date}
              onChange={(e) => setNewOrder({...newOrder, order_received_date: e.target.value})}
              data-testid="add-order-received-date"
            />
            <Input
              placeholder="Notes/Narration (Optional)"
              value={newOrder.notes}
              onChange={(e) => setNewOrder({...newOrder, notes: e.target.value})}
              data-testid="add-order-notes"
            />
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
            <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="add-order-submit">
              Save Order
            </Button>
          </div>
          </form>
          )}

          {filteredAndSortedOrders.length === 0 ? (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">No orders available</p>
              <p className="text-slate-500 text-sm mt-2">Add orders manually or upload WINLIFE sheets</p>
            </div>
          ) : (
            <div className="print-content">
              <div className="hidden print:block p-4 border-b">
                <h1 className="text-xl font-bold">Orders Report</h1>
                <p className="text-sm text-gray-600">Total Orders: {filteredAndSortedOrders.length}</p>
              </div>
              
              {/* Scrollable Table View */}
              <div className="overflow-x-auto" style={{ maxHeight: '70vh' }}>
                <table className="w-full" data-testid="orders-table">
                  <thead className="sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-200 shadow-sm print:static print:bg-white">
                    <tr>
                      {(user.role === 'admin' || user.role === 'winlife') && <th className="px-4 py-3 text-xs font-semibold text-slate-700 uppercase no-print">Actions</th>}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase cursor-pointer hover:bg-slate-100" onClick={() => handleSort('product_name')}>
                  <div className="flex items-center space-x-1">
                    <span>Product Name</span>
                    {getSortIndicator('product_name') || <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase cursor-pointer hover:bg-slate-100" onClick={() => handleSort('order_given_to')}>
                  <div className="flex items-center space-x-1">
                    <span>Order Given To</span>
                    {getSortIndicator('order_given_to') || <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase cursor-pointer hover:bg-slate-100" onClick={() => handleSort('order_qty')}>
                  <div className="flex items-center space-x-1">
                    <span>Order Qty</span>
                    {getSortIndicator('order_qty') || <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase cursor-pointer hover:bg-slate-100" onClick={() => handleSort('order_given_date')}>
                  <div className="flex items-center space-x-1">
                    <span>Order Given Date</span>
                    {getSortIndicator('order_given_date') || <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase cursor-pointer hover:bg-slate-100" onClick={() => handleSort('order_age')}>
                  <div className="flex items-center justify-center space-x-1">
                    <span>Age (Days)</span>
                    {getSortIndicator('order_age') || <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Status / Received</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Actions</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Notes/Narration</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredAndSortedOrders.map((order, index) => {
                const isEditing = editingId === order.id;
                return (
                  <tr key={order.id} className="hover:bg-slate-50 transition-colors" data-testid={`order-row-${index}`}>
                    {(user.role === 'admin' || user.role === 'winlife') && (
                      <td className="px-4 py-3 no-print">
                        {!isEditing ? (
                          <div className="flex space-x-1">
                            <Button onClick={() => startEdit(order)} size="sm" variant="outline" className="h-8">
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                            <Button onClick={() => deleteOrder(order.id)} size="sm" variant="destructive" className="h-8">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex space-x-1">
                            <Button onClick={() => saveEdit(order.id)} size="sm" className="h-8 bg-green-600 hover:bg-green-700">
                              <Save className="w-3.5 h-3.5" />
                            </Button>
                            <Button onClick={cancelEdit} size="sm" variant="outline" className="h-8">
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm text-slate-800 font-medium">
                      {isEditing ? (
                        <Input value={editData.product_name} onChange={(e) => setEditData({...editData, product_name: e.target.value})} className="h-9" />
                      ) : order.product_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {isEditing ? (
                        <Input value={editData.order_given_to} onChange={(e) => setEditData({...editData, order_given_to: e.target.value.toUpperCase()})} className="h-9" />
                      ) : order.order_given_to}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {isEditing ? (
                        <Input type="number" value={editData.order_qty} onChange={(e) => setEditData({...editData, order_qty: parseFloat(e.target.value)})} className="h-9" />
                      ) : order.order_qty}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {isEditing ? (
                        <Input type="date" value={editData.order_given_date} onChange={(e) => setEditData({...editData, order_given_date: e.target.value})} className="h-9" />
                      ) : order.order_given_date}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {/* Order Age in Days */}
                      {(() => {
                        const age = getOrderAge(order.order_given_date);
                        const badgeColor = getAgeBadgeColor(age);
                        return (
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${badgeColor}`}>
                            {age !== null ? age : '-'}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      {/* Status Badge & Progress */}
                      {(() => {
                        const status = getStatusBadge(order);
                        const received = order.received_qty || 0;
                        const total = order.order_qty || 0;
                        const progress = total > 0 ? (received / total) * 100 : 0;
                        const StatusIcon = status.icon;
                        
                        return (
                          <div className="space-y-1">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {status.label}
                            </span>
                            {total > 0 && (
                              <div className="w-24">
                                <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full transition-all ${
                                      progress >= 100 ? 'bg-green-500' : progress > 0 ? 'bg-orange-500' : 'bg-slate-300'
                                    }`}
                                    style={{ width: `${Math.min(progress, 100)}%` }}
                                  />
                                </div>
                                <p className="text-[10px] text-slate-500 mt-0.5">
                                  {received.toLocaleString()}/{total.toLocaleString()}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      {/* Receive Stock Button */}
                      <Button
                        onClick={() => openReceiveModal(order)}
                        size="sm"
                        className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                        data-testid={`receive-stock-${index}`}
                      >
                        <Package className="w-3.5 h-3.5 mr-1" />
                        Receive Stock
                      </Button>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {isEditing ? (
                        <Input 
                          value={editData.notes || ''} 
                          onChange={(e) => setEditData({...editData, notes: e.target.value})} 
                          className="h-9"
                          placeholder="Add notes..."
                        />
                      ) : (order.notes || '-')}
                    </td>
                  </tr>
                );
              })}
                </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Supplier Orders Report Modal */}
      {showSupplierReport && (
        <SupplierOrdersReport 
          orders={orders} 
          stockData={stockData}
          onClose={() => setShowSupplierReport(false)} 
        />
      )}

      {/* Receive Stock Modal */}
      {showReceiveModal && receivingOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Receive Stock
                </h3>
                <Button
                  onClick={() => setShowReceiveModal(false)}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-indigo-100 text-sm mt-1">{receivingOrder.product_name}</p>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Order Summary */}
              <div className="grid grid-cols-3 gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-xs text-slate-500">Ordered</p>
                  <p className="text-lg font-bold text-slate-800">{receivingOrder.order_qty?.toLocaleString()}</p>
                </div>
                <div className="text-center border-x border-slate-200">
                  <p className="text-xs text-slate-500">Received</p>
                  <p className="text-lg font-bold text-green-600">{(receivingOrder.received_qty || 0).toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500">Remaining</p>
                  <p className="text-lg font-bold text-orange-600">
                    {((receivingOrder.order_qty || 0) - (receivingOrder.received_qty || 0)).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              {(() => {
                const received = receivingOrder.received_qty || 0;
                const total = receivingOrder.order_qty || 0;
                const progress = total > 0 ? (received / total) * 100 : 0;
                return (
                  <div>
                    <div className="flex justify-between text-xs text-slate-600 mb-1">
                      <span>Progress</span>
                      <span>{progress.toFixed(0)}%</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          progress >= 100 ? 'bg-green-500' : progress > 0 ? 'bg-orange-500' : 'bg-slate-300'
                        }`}
                        style={{ width: `${Math.min(progress, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })()}

              {/* Receipt History Toggle */}
              {receivingOrder.receipts && receivingOrder.receipts.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowReceiptHistory(!showReceiptHistory)}
                    className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    <History className="w-4 h-4" />
                    View Receipt History ({receivingOrder.receipts.length})
                    <ChevronDown className={`w-4 h-4 transition-transform ${showReceiptHistory ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showReceiptHistory && (
                    <div className="mt-2 max-h-32 overflow-y-auto border border-slate-200 rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left">Date</th>
                            <th className="px-2 py-1 text-right">Qty</th>
                            <th className="px-2 py-1 text-left">Notes</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {receivingOrder.receipts.map((r, i) => (
                            <tr key={i}>
                              <td className="px-2 py-1">{r.date}</td>
                              <td className="px-2 py-1 text-right font-medium">{r.qty?.toLocaleString()}</td>
                              <td className="px-2 py-1 text-slate-500 truncate max-w-[100px]">{r.notes || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Input Fields */}
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Receiving Quantity</label>
                  <Input
                    type="number"
                    value={receiveQty}
                    onChange={(e) => setReceiveQty(parseFloat(e.target.value) || 0)}
                    placeholder="Enter quantity received"
                    className="w-full"
                    min="0"
                    max={(receivingOrder.order_qty || 0) - (receivingOrder.received_qty || 0)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Date Received</label>
                  <Input
                    type="date"
                    value={receiveDate}
                    onChange={(e) => setReceiveDate(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Notes (Optional)</label>
                  <Input
                    type="text"
                    value={receiveNotes}
                    onChange={(e) => setReceiveNotes(e.target.value)}
                    placeholder="e.g., Batch #123, First delivery"
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t flex flex-wrap gap-2">
              <Button
                onClick={() => setShowReceiveModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReceivePartial}
                disabled={receiveQty <= 0}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                <Package className="w-4 h-4 mr-1" />
                Receive Partial
              </Button>
              <Button
                onClick={handleReceiveAll}
                disabled={(receivingOrder.received_qty || 0) >= (receivingOrder.order_qty || 0)}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Receive All
              </Button>
              {(receivingOrder.received_qty || 0) > 0 && (receivingOrder.received_qty || 0) < (receivingOrder.order_qty || 0) && (
                <Button
                  onClick={handleCompletePartial}
                  variant="outline"
                  className="w-full mt-2 border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Mark Complete (Satisfied with Partial)
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual Suggestion Modal */}
      {showManualSuggestionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add Manual Suggestion
                </h3>
                <Button
                  onClick={() => setShowManualSuggestionModal(false)}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-purple-100 text-sm mt-1">Add a product suggestion from team member</p>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <div className="relative">
                <label className="text-sm font-medium text-slate-700 block mb-1">Product Name *</label>
                <div className="relative">
                  <Input
                    type="text"
                    value={newManualSuggestion.product_name}
                    onChange={(e) => handleProductNameChange(e.target.value)}
                    onFocus={() => newManualSuggestion.product_name.length >= 3 && setShowProductDropdown(true)}
                    placeholder="Start typing product name (min 3 chars)..."
                    className="w-full"
                    autoComplete="off"
                  />
                  {searchingProducts && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                
                {/* Product Autocomplete Dropdown */}
                {showProductDropdown && productSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {productSuggestions.map((product, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => selectProduct(product)}
                        className="w-full px-3 py-2 text-left hover:bg-indigo-50 flex items-center justify-between border-b border-slate-100 last:border-0"
                      >
                        <span className="font-medium text-slate-800">{product.product_name}</span>
                        <span className="text-xs text-slate-500">
                          Stock: {product.current_stock?.toLocaleString() || 0}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                
                {newManualSuggestion.product_name.length > 0 && newManualSuggestion.product_name.length < 3 && (
                  <p className="text-xs text-slate-400 mt-1">Type at least 3 characters to see suggestions</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Suggested Qty *</label>
                  <Input
                    type="number"
                    value={newManualSuggestion.suggested_qty}
                    onChange={(e) => setNewManualSuggestion(prev => ({ ...prev, suggested_qty: parseFloat(e.target.value) || 0 }))}
                    placeholder="Quantity"
                    min="0"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Priority</label>
                  <select
                    value={newManualSuggestion.priority}
                    onChange={(e) => setNewManualSuggestion(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full h-10 px-3 border border-slate-300 rounded-md"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>

              <div className="relative">
                <label className="text-sm font-medium text-slate-700 block mb-1">Order To (Supplier)</label>
                <Input
                  type="text"
                  value={newManualSuggestion.order_to}
                  onChange={(e) => handleManualSupplierChange(e.target.value)}
                  onFocus={() => newManualSuggestion.order_to.length >= 2 && setShowSupplierDropdown(true)}
                  onBlur={() => setTimeout(() => setShowSupplierDropdown(false), 200)}
                  placeholder="Supplier name (min 2 chars)"
                  className="w-full"
                  autoComplete="off"
                />
                {searchingSuppliers && (
                  <div className="absolute right-3 top-9 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {/* Supplier Autocomplete Dropdown */}
                {showSupplierDropdown && supplierSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {supplierSuggestions.map((supplier, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => selectManualSupplier(supplier)}
                        className="w-full px-3 py-2 text-left hover:bg-purple-50 flex items-center justify-between border-b border-slate-100 last:border-0"
                      >
                        <span className="font-medium text-slate-800">{supplier.name}</span>
                        {supplier.has_ledger && (
                          <span className={`text-xs ${supplier.ledger_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            Bal: {supplier.ledger_balance?.toLocaleString() || 0}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {newManualSuggestion.order_to.length > 0 && newManualSuggestion.order_to.length < 2 && (
                  <p className="text-xs text-slate-400 mt-1">Type at least 2 characters</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Suggested By</label>
                <Input
                  type="text"
                  value={newManualSuggestion.suggested_by}
                  onChange={(e) => setNewManualSuggestion(prev => ({ ...prev, suggested_by: e.target.value }))}
                  placeholder="Team member name"
                  className="w-full"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Reason / Notes</label>
                <textarea
                  value={newManualSuggestion.reason}
                  onChange={(e) => setNewManualSuggestion(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Why is this order needed? Any special notes..."
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t flex gap-3">
              <Button
                onClick={() => setShowManualSuggestionModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddManualSuggestion}
                disabled={!newManualSuggestion.product_name || newManualSuggestion.suggested_qty <= 0}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Suggestion
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
